// Helpers de formatação — portados do dashboard antigo (pt-BR).

export function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "string") n = parseFloat(n);
  if (Number.isNaN(n)) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(".", ",")} K`;
  return n.toLocaleString("pt-BR");
}

/** Número em pt-BR com 1 casa decimal (nota). */
export function brPt(n) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "string" ? parseFloat(n) : n;
  if (Number.isNaN(v)) return "—";
  return v.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}

/** Duração dinâmica: "45 min", "2 h". */
export function fmtTempo(seg) {
  const s = Number(seg) || 0;
  const h = Math.floor(s / 3600);
  if (h >= 1000) return `${fmt(Math.round(h / 1000) * 1000)} h`;
  if (h > 0) return `${fmt(h)} h`;
  const m = Math.floor(s / 60);
  return `${fmt(m)} min`;
}

/** Cor da nota (0–10) por faixa de valor, na variante VIVA.
 *
 * Para o número grande do KpiCard (28px bold, que WCAG conta como texto grande
 * e pede só 3:1). Em texto de tamanho normal use `gradeColorTexto` — o amarelo
 * vivo tem 3,25:1 e reprovaria. */
export function gradeColor(n) {
  if (n === null || n === undefined || Number.isNaN(n))
    return "var(--color-ink-faint)";
  if (n >= 7) return "var(--color-good)";
  if (n >= 5) return "var(--color-warn)";
  return "var(--color-bad)";
}

/** A mesma escala de `gradeColor`, na variante escura (-ink), para NOTA EM
 * TEXTO PEQUENO: célula de tabela, linha de ranking, popover.
 *
 * Existe porque as cores de status foram deixadas vivas a pedido do Kilson
 * (05/08/2026) e viva é cor de preenchimento: o amarelo #b8860b tem 3,25:1
 * contra o card branco, o que basta para uma fatia de donut ou um número de
 * 28px, e não para os 13px de uma tabela. Ver o bloco de status em tokens.css. */
export function gradeColorTexto(n) {
  if (n === null || n === undefined || Number.isNaN(n))
    return "var(--color-ink-faint)";
  if (n >= 7) return "var(--color-good-ink)";
  if (n >= 5) return "var(--color-warn-ink)";
  return "var(--color-bad-ink)";
}

/** Faixa do score de engajamento (0–100) — limiares (40/35) calibrados pela
 * distribuição real do score (pilar ausente conta 0, então a média típica é
 * baixa). Fonte única de verdade: usado tanto no engajamento do aluno
 * (professor > AlunosTabela) quanto em qualquer ranking agregado (turma,
 * escola, região). */
export function engajamentoTone(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "none";
  if (n >= 40) return "good";
  if (n >= 35) return "warn";
  return "bad";
}

/** Cor do score de engajamento, derivada de engajamentoTone. */
export function engajamentoColor(n) {
  const tone = engajamentoTone(n);
  if (tone === "good") return "var(--color-good)";
  if (tone === "warn") return "var(--color-warn)";
  if (tone === "bad") return "var(--color-bad)";
  return "var(--color-ink-faint)";
}

/** Normaliza o rótulo pt-BR da faixa vindo do backend para uma chave interna. */
export function faixaKey(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("acima")) return "acima";
  if (l.includes("abaixo")) return "abaixo";
  if (l.includes("média") || l.includes("media")) return "media";
  return "sem_referencia";
}

export const FAIXA_META = {
  acima: { label: "Acima da média", cls: "good", rank: 0 },
  media: { label: "Na média", cls: "warn", rank: 1 },
  abaixo: { label: "Abaixo da média", cls: "bad", rank: 2 },
  sem_referencia: { label: "Sem referência", cls: "none", rank: 3 }
};

/** Último acesso legível a partir de "dias desde o último acesso". */
export function acessoLabel(dias) {
  if (dias === null || dias === undefined || dias >= 999)
    return { text: "Nunca", tone: "bad" };
  if (dias === 0) return { text: "hoje", tone: "ok" };
  if (dias === 1) return { text: "ontem", tone: "ok" };
  return { text: `${dias} dias atrás`, tone: dias > 7 ? "warn" : "ok" };
}

/** Iniciais para o avatar. */
export function initials(nome) {
  const parts = (nome || "").trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AV_COLORS = [
  "#0d9488",
  "#4f46e5",
  "#7c3aed",
  "#e15759",
  "#f28e2b",
  "#59a14f",
  "#b07aa1",
  "#4e79a7",
  "#76b7b2",
  "#9c755f"
];

export function avatarColor(i) {
  return AV_COLORS[i % AV_COLORS.length];
}

/* Cores usadas quando uma série de gráfico não declara `color` própria.

   Era a Tableau 10, herdada do projeto de origem, e ela reprovava feio no
   validador de paleta: o par verde/vermelho (#59a14f × #e15759) dava ΔE 0,7 em
   deuteranopia — indistinguíveis — e 12,8 em visão NORMAL, abaixo do piso de 15.
   Num painel lido por professores, gestores e secretários, com deuteranopia em
   torno de 8% dos homens, isso é gente que não consegue ler o gráfico.

   Agora são os mesmos quatro valores de --color-chart-1..4 (definidos em
   styles/componentes.css), que passam os cinco checks. Manter as duas listas
   iguais é intencional: antes o
   painel tinha DUAS paletas independentes, e a mesma série podia sair de uma cor
   ou de outra conforme declarasse `color` ou não.

   Quatro, e não oito, pelo motivo explicado em tokens.css — azul/violeta e
   teal/verde colapsam entre si e não há oito hues mutuamente distinguíveis. Os
   consumidores devem agrupar em "Outros" além do quarto, como faz o gráfico por
   tipo de atividade do TurmaPainel. */
export const CHART_COLORS = ["#00968c", "#d97706", "#2563eb", "#b91c1c"];

/** Formata um período ISO (yyyy-mm-dd) conforme a granularidade do gráfico. */
export function formatPeriodo(iso, gran) {
  const [y, m, dd] = String(iso).slice(0, 10).split("-").map(Number);
  const d = new Date(y, (m || 1) - 1, dd || 1);
  if (gran === "dia")
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  if (gran === "semana")
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return d.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
}

const SERIE_REGEX = /^(\d+[ºª]\s*(?:ano|s[ée]rie(?:\s*em)?))/i;

/** Extrai a série (ex.: "6º Ano", "2ª Série EM") do nome da turma (ex.: "6º Ano
 * B"). O nome vem como texto livre (sem um campo de série dedicado na origem),
 * então isso é uma heurística sobre o prefixo do nome — turmas que não seguem
 * esse padrão caem em "Outras". */
export function extrairSerie(nomeTurma) {
  const m = (nomeTurma || "").match(SERIE_REGEX);
  return m ? m[1].replace(/\s+/g, " ").trim() : "Outras";
}

/** Normaliza string/number vindo do backend (vírgula decimal) pra number ou null. */
export function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const n = Number(value.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
